import {
  type SortingState,
  useDataTable,
} from '@ovComponents/2-component/dataTable';
import { UserContext, usePermission } from '@providers/userContextProvider';
import type { OnChangeFn } from '@tanstack/react-table';
import {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTradingHub } from '../tradingHubContext';
import { DENSITY_CONFIG } from '../utils';
import { downloadSelectedPortfoliosCsv } from '../utils/downloadSelectedPortfoliosCsv';
import type { PortfolioExportTranslate } from '../utils/portfolioExportConfig';
import { STATUS_FILTER_MAP } from '../utils/portfolioExportConfig';
import type { ViewMode } from '../utils/portfolioFilters';
import { getPortfolioSortVariables } from '../utils/portfolioSorting';
import {
  shouldResetPage,
  shouldShowPortfolioPagination,
} from '../utils/portfolioTabPagination';
import { useExposureSearchOptions } from './useExposureSearchOptions';
import { useModelPortfolioFilterOptions } from './useModelPortfolioFilterOptions';
import { useModelViewSearchOptions } from './useModelViewSearchOptions';
import { usePortfolioBulkActions } from './usePortfolioBulkActions';
import { usePortfolioColumns } from './usePortfolioColumns';
import { usePortfolioDeepLink } from './usePortfolioDeepLink';
import { usePortfolioDisplayedRows } from './usePortfolioDisplayedRows';
import { usePortfolioDriftTolerance } from './usePortfolioDriftTolerance';
import { usePortfolioFilters } from './usePortfolioFilters';
import { usePortfolioListQuery } from './usePortfolioListQuery';
import { usePortfolioProgressiveRows } from './usePortfolioProgressiveRows';
import { usePortfolioReportSearchOptions } from './usePortfolioReportSearchOptions';
import type {
  EnrichedPortfolioReport,
  UsePortfolioTableStateResult,
} from './usePortfolioTableState.types';
export function usePortfolioTableState(): UsePortfolioTableStateResult {
  const { activeOrganization } = useContext(UserContext);
  const canReadRepCode = usePermission('read:rep_code');
  const canExport = usePermission('read:api_exports');
  const { density, setActiveFlowStep } = useTradingHub();
  const { t } = useTranslation('portfolioTradingHub');
  const densityConfig = DENSITY_CONFIG[density];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [viewMode, setViewMode] = useState<ViewMode>('portfolios');
  const [sorting, setSorting] = useState<SortingState>([]);

  // Rows are paginated server-side, so a new sort has to start from page 1 to
  // avoid landing on an offset from the previous ordering.
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting((previous) =>
        typeof updaterOrValue === 'function'
          ? updaterOrValue(previous)
          : updaterOrValue,
      );
      setPage(1);
    },
    [],
  );

  const { sortField, sortDesc } = useMemo(
    () => getPortfolioSortVariables(sorting),
    [sorting],
  );

  const filters = usePortfolioFilters({
    setPage,
    setPageSize,
    setViewMode,
  });

  const exportFilter = useMemo(() => {
    const activeStatusKey = STATUS_FILTER_MAP[filters.statusFilter];
    const trimmedSearch = filters.debouncedSearch.trim();

    return {
      organizationId: activeOrganization?.id,
      ...(activeStatusKey && { [activeStatusKey]: true }),
      modelPortfolioType:
        filters.portfolioTypeFilter !== 'ANY'
          ? filters.portfolioTypeFilter
          : undefined,
      modelPortfolioIds:
        filters.modelFilter.length > 0 ? filters.modelFilter : undefined,
      ...(trimmedSearch ? { searchText: trimmedSearch } : {}),
    };
  }, [
    activeOrganization?.id,
    filters.statusFilter,
    filters.portfolioTypeFilter,
    filters.modelFilter,
    filters.debouncedSearch,
  ]);

  const listQuery = usePortfolioListQuery({
    organizationId: activeOrganization?.id,
    page,
    pageSize,
    debouncedSearch: filters.debouncedSearch,
    statusFilter: filters.statusFilter,
    portfolioTypeFilter: filters.portfolioTypeFilter,
    modelFilter: filters.modelFilter,
    viewMode,
    canReadRepCode,
    sortField,
    sortDesc,
  });

  const driftTolerancePercent = usePortfolioDriftTolerance(
    activeOrganization?.id,
  );

  const displayedRows = usePortfolioDisplayedRows({
    portfolios: listQuery.portfolios,
    debouncedSearch: filters.debouncedSearch,
    modelFilter: filters.modelFilter,
    isClientFilter: listQuery.isClientFilter,
    viewMode,
    page,
    pageSize,
    totalCount: listQuery.totalCount,
  });

  useEffect(() => {
    if (shouldResetPage(page, displayedRows.totalPages)) {
      setPage(1);
    }
  }, [displayedRows.totalPages, page]);

  const deepLink = usePortfolioDeepLink({
    organizationId: activeOrganization?.id,
    portfolios: listQuery.portfolios,
    canReadRepCode,
  });

  const bulkActions = usePortfolioBulkActions({
    organizationId: activeOrganization?.id,
    bulkTradingProcess: activeOrganization?.bulkTradingProcess,
    filteredPortfolios: displayedRows.filteredPortfolios,
    displayedPortfolios: displayedRows.displayedPortfolios,
    viewMode,
  });

  const {
    options: modelOptions,
    loading: modelOptionsLoading,
    onSearchModels,
  } = useModelPortfolioFilterOptions({
    organizationId: activeOrganization?.id,
    selectedModelIds: filters.modelFilter,
    selectedModelNames: filters.selectedModelNames,
  });
  const {
    onSearch: onSearchPortfolioReports,
    onSearchError: onPortfolioSearchError,
  } = usePortfolioReportSearchOptions({
    organizationId: activeOrganization?.id,
    canReadRepCode,
  });
  const { onSearch: onSearchExposure, onSearchError: onExposureSearchError } =
    useExposureSearchOptions({
      organizationId: activeOrganization?.id,
    });
  const { onSearch: onSearchModelView, onSearchError: onModelViewSearchError } =
    useModelViewSearchOptions({
      organizationId: activeOrganization?.id,
    });

  const portfolioColumns = usePortfolioColumns({ canReadRepCode });
  const { table: portfolioTable, columnFilters } = useDataTable({
    data: displayedRows.displayedPortfolios,
    columns: portfolioColumns,
    sorting,
    onSortingChange: handleSortingChange,
    sortingMode: 'manual',
    enableFiltering: true,
  });

  const allRows = portfolioTable.getRowModel().rows;
  const sortingKey = JSON.stringify(sorting);
  const filtersKey = JSON.stringify(columnFilters);

  const progressiveRows = usePortfolioProgressiveRows({
    allRows,
    expandedPortfolioId: deepLink.expandedPortfolioId,
    sortingKey,
    filtersKey,
    page,
    pageSize,
    displayedCount: displayedRows.displayedPortfolios.length,
    displayedFirstId: displayedRows.displayedPortfolios[0]?.id,
  });

  const handlePageChange = useCallback((newPage: number) => {
    startTransition(() => setPage(newPage));
  }, []);

  const handleGoBackPage = useCallback(() => {
    startTransition(() => setPage((p) => Math.max(1, p - 1)));
  }, []);

  const handleRetryPage = useCallback(() => {
    listQuery.refetch();
  }, [listQuery.refetch]);

  const closeModelStaging = useCallback(() => {
    bulkActions.setModelStagingPortfolios(null);
    setActiveFlowStep('idle');
  }, [bulkActions.setModelStagingPortfolios, setActiveFlowStep]);

  const onModelBulkTradesCreated = useCallback(() => {
    bulkActions.setModelStagingPortfolios(null);
    bulkActions.completeRebalanceFlow({
      clearSelection: false,
      closeReviewCommit: false,
    });
  }, [
    bulkActions.setModelStagingPortfolios,
    bulkActions.completeRebalanceFlow,
  ]);

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      if (mode === viewMode) return;
      filters.setSearchTerm('');
      setViewMode(mode);
    },
    [viewMode, filters.setSearchTerm],
  );

  const onSearchOptions = useCallback(
    async (query: string) => {
      switch (viewMode) {
        case 'portfolios':
          return onSearchPortfolioReports(query);
        case 'exposure':
          return onSearchExposure(query);
        case 'model':
          return onSearchModelView(query);
      }
    },
    [viewMode, onSearchPortfolioReports, onSearchExposure, onSearchModelView],
  );

  const onSearchOptionsError = useCallback(
    (error: unknown) => {
      switch (viewMode) {
        case 'portfolios':
          onPortfolioSearchError(error);
          break;
        case 'exposure':
          onExposureSearchError(error);
          break;
        case 'model':
          onModelViewSearchError(error);
          break;
      }
    },
    [
      viewMode,
      onPortfolioSearchError,
      onExposureSearchError,
      onModelViewSearchError,
    ],
  );

  const hasPageLoadError =
    listQuery.isPageReady && listQuery.pageGraphQLErrors.length > 0;

  return {
    loading: listQuery.loading,
    hasRows: listQuery.portfolios.length > 0,
    fatalErrorMessage: listQuery.fatalErrorMessage,
    viewMode,
    density,
    toolbarProps: {
      viewMode,
      searchTerm: filters.searchTerm,
      onSearchChange: filters.setSearchTerm,
      onSearchOptions,
      onSearchOptionsError,
      statusFilter: filters.statusFilter,
      onStatusChange: filters.handleFilterChange(filters.setStatusFilter),
      portfolioTypeFilter: filters.portfolioTypeFilter,
      onPortfolioTypeChange: filters.handleFilterChange(
        filters.setPortfolioTypeFilter,
      ),
      modelFilter: filters.modelFilter,
      onModelChange: filters.handleModelChange,
      modelOptions,
      onSearchModels,
      modelOptionsLoading,
      hasActiveFilters: filters.hasActiveFilters,
      onClearFilters: filters.handleClearFilters,
      organizationId: activeOrganization?.id,
      exportFilter,
      canExport,
    },
    tableProps: {
      portfolioTable,
      displayedPortfolios: displayedRows.displayedPortfolios,
      rowsToRender: progressiveRows.rowsToRender,
      ghostReport: deepLink.ghostReport,
      selectedPortfolios: bulkActions.selectedPortfolios,
      onSelectAll: bulkActions.handleSelectAll,
      onToggleSelect: bulkActions.handleToggleSelect,
      expandedPortfolioId: deepLink.expandedPortfolioId,
      onSetExpanded: deepLink.handleSetExpanded,
      densityConfig,
      isPageLoading: listQuery.loading,
      isPageReady: listQuery.isPageReady,
      hasPageLoadError,
      pageGraphQLErrors: listQuery.pageGraphQLErrors,
      page,
      onPageChange: handlePageChange,
      onRetryPage: handleRetryPage,
      onGoBackPage: handleGoBackPage,
      totalPages: displayedRows.totalPages,
      totalCount: listQuery.totalCount,
      showPagination: shouldShowPortfolioPagination(
        listQuery.isClientFilter,
        displayedRows.filteredPortfolios.length,
        listQuery.totalCount,
      ),
      pageSize,
      onPageSizeChange: filters.handlePageSizeChange,
      unresolvedRowCount: 0,
      hasActiveFilters: filters.hasActiveFilters,
      effectiveRenderCount: progressiveRows.effectiveRenderCount,
      visibleRowCount: progressiveRows.visibleRowCount,
      organizationId: activeOrganization?.id || '',
      bulkTradingProcess: activeOrganization?.bulkTradingProcess,
      onTradeComplete: bulkActions.handleTradeComplete,
      pinnedPortfolioReportId: deepLink.pinnedPortfolioReportId,
      canReadRepCode,
      driftTolerancePercent,
    },
    shellProps: {
      viewMode,
      onViewModeChange: handleViewModeChange,
      filteredCount: displayedRows.filteredPortfolios.length,
    },
    selectionBarProps:
      bulkActions.selectedPortfolios.size > 0 && !bulkActions.showReviewCommit
        ? {
            selectedCount: bulkActions.selectedPortfolios.size,
            onRebalanceSelected: () => bulkActions.setShowReviewCommit(true),
            onDownloadSelected: () =>
              downloadSelectedPortfoliosCsv(
                bulkActions.selectedPortfolioReports,
                t as PortfolioExportTranslate,
              ),
            onClear: () => bulkActions.setSelectedPortfolios(new Set()),
          }
        : null,
    reviewCommitProps: bulkActions.showReviewCommit
      ? {
          selectedPortfolios: bulkActions.selectedPortfolioReports,
          organizationId: activeOrganization?.id || '',
          bulkTradingProcess: activeOrganization?.bulkTradingProcess,
          onTradesCreated: () =>
            bulkActions.completeRebalanceFlow({ clearSelection: true }),
          onCancel: () => bulkActions.setShowReviewCommit(false),
        }
      : null,
    modelBulkStagingProps:
      bulkActions.modelStagingPortfolios &&
      bulkActions.modelStagingPortfolios.length > 0
        ? {
            selectedPortfolios:
              bulkActions.modelStagingPortfolios as EnrichedPortfolioReport[],
            organizationId: activeOrganization?.id || '',
            onClose: closeModelStaging,
            onTradesCreated: onModelBulkTradesCreated,
          }
        : null,
    exposureViewProps: {
      exposures: bulkActions.aggregateExposures,
      searchTerm: filters.searchTerm,
      onCreateTrades: bulkActions.handleCreateTradesFromExposure,
    },
    modelViewProps: {
      models: bulkActions.modelAggregations,
      searchTerm: filters.searchTerm,
      onRebalanceSelected: bulkActions.setModelStagingPortfolios,
    },
  };
}
